<template>
  <ui-dialog
    :show="isOpen"
    persistent
    scrollable
    max-width="623"
    custom-class="keywords-dialog"
    @input="close"
  >
    <template #header>
      <header class="header">
        <h3>
          {{
            $t(
              `message.analytics.periodic.keywords.dialog.${
                isTableAverageReport ? PERIODIC_REPORT_TYPE.TOP : reportType
              }.title`
            )
          }}
        </h3>
      </header>

      <v-text-field
        v-model="keywordsSearchInputValue"
        :placeholder="
          $t(
            'message.analytics.periodic.keywords.dialog.filterInputPlaceholder'
          )
        "
        prepend-inner-icon="icon-search"
        clearable
        outlined
        rounded
        :hide-details="true"
      ></v-text-field>
      <InfoBox
        v-if="!canSelectMultipleKeywords"
        class="mt-2"
        iconColor="pink"
        is-html
        :text="
          $t(
            `message.analytics.periodic.keywords.dialog.${PERIODIC_REPORT_TYPE.AVERAGE}.infoBox`
          )
        "
      />
    </template>
    <template #default>
      <div class="dialog-body">
        <v-list v-if="filteredKeywords && filteredKeywords.length">
          <v-list-item
            v-if="canSelectMultipleKeywords"
            ripple
            class="list-item list-item--all"
            @click="toggleAllKeywords"
          >
            <v-checkbox :inputValue="isAllKeywordsSelected"></v-checkbox>
            <v-list-item-content>
              <v-list-item-title>
                <p class="list-item__name">
                  {{ $t("message.common.selectAll") }}
                </p>
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <ItemsCounter
                v-if="
                  isTopReport && selectedKeywords && selectedKeywords.length
                "
                :count="selectedKeywords.length"
              />
            </v-list-item-action>
          </v-list-item>

          <v-virtual-scroll
            v-if="canSelectMultipleKeywords"
            :items="filteredKeywords"
            :item-height="listItemHeight"
            :height="listHeight"
          >
            <template v-slot:default="{ item }">
              <v-list-item
                :key="item.keyword.id"
                ripple
                @click="toggleKeyword(item)"
                class="list-item"
                :class="{ 'list-item--top': isTopReport }"
              >
                <v-checkbox
                  :inputValue="selectedKeywordsIds.includes(item.keyword.id)"
                ></v-checkbox>
                <v-list-item-content>
                  <p class="list-item__name">{{ item.keyword.name }}</p>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-virtual-scroll>

          <v-radio-group
            v-else-if="!canSelectMultipleKeywords"
            v-model="selectedKeywordId"
          >
            <v-virtual-scroll
              :items="filteredKeywords"
              :item-height="listItemHeight"
              :height="listHeight"
            >
              <template v-slot:default="{ item }">
                <v-list-item
                  :key="item.keyword.id"
                  ripple
                  @click="selectKeyword(item)"
                  class="list-item"
                >
                  <v-list-item-content>
                    <p class="list-item__name">{{ item.keyword.name }}</p>
                  </v-list-item-content>
                  <v-radio class="mb-0" :value="item.keyword.id"></v-radio>
                </v-list-item>
              </template>
            </v-virtual-scroll>
          </v-radio-group>
        </v-list>
        <no-data v-else></no-data>

        <SectionLoader :status="loading" :opacity="0.75" />
      </div>
    </template>
    <template #actions>
      <div class="w-100 d-flex justify-end">
        <ui-button
          color="primary"
          :disabled="!canSaveTop && !canSaveAverage"
          @click="save"
        >
          {{ $t("message.common.save") }}
        </ui-button>
      </div>
    </template>
  </ui-dialog>
</template>

<script>
import { mapState } from "vuex";

import ItemsCounter from "@/components/List/ItemsCounter";
import InfoBox from "@/components/ui/InfoBox.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import NoData from "@/components/ui/NoData";

import { PERIODIC_REPORT_TYPE } from "@/enums/charts-type.enum";

export default {
  name: "KeywordsDialog",
  components: { ItemsCounter, InfoBox, SectionLoader, NoData },
  data() {
    return {
      PERIODIC_REPORT_TYPE,
      loading: false,
      selectedKeywords: [],
      selectedKeywordId: null,
      keywordsSearchInputValue: ""
    };
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    initiallySelectedKeywords: {
      type: Array,
      default: () => []
    },
    reportType: {
      type: Number,
      validator: val => Object.values(PERIODIC_REPORT_TYPE).includes(val)
    },
    isTableRoute: {
      type: Boolean,
      default: true
    }
  },

  computed: {
    ...mapState({
      keywords: state => state.keywords.keywords
    }),
    isTopReport() {
      return this.reportType === PERIODIC_REPORT_TYPE.TOP;
    },
    isAverageReport() {
      return this.reportType === PERIODIC_REPORT_TYPE.AVERAGE;
    },
    isTableAverageReport() {
      return this.isAverageReport && this.isTableRoute;
    },
    canSelectMultipleKeywords() {
      return this.isTopReport || this.isTableAverageReport;
    },
    canSaveTop() {
      return (
        this.isTopReport &&
        this.selectedKeywords &&
        !!this.selectedKeywords.length
      );
    },
    canSaveAverage() {
      return (
        (this.isAverageReport &&
          !this.isTableRoute &&
          this.selectedKeywords &&
          this.selectedKeywords.length === 1) ||
        (this.isTableRoute &&
          this.selectedKeywords &&
          !!this.selectedKeywords.length)
      );
    },
    keywordsIds() {
      return this.keywords.map(({ keyword }) => keyword.id);
    },
    filteredKeywordsIds() {
      return this.filteredKeywords.map(({ keyword }) => keyword.id);
    },
    selectedKeywordsIds() {
      return this.selectedKeywords.map(({ keyword }) => keyword.id);
    },
    isAllKeywordsSelected() {
      return this.filteredKeywordsIds.every(id =>
        this.selectedKeywordsIds.includes(id)
      );
    },
    filteredKeywords() {
      return this.keywords.filter(({ keyword }) =>
        keyword.name.includes(this.keywordsSearchInputValue)
      );
    },
    listItemHeight() {
      return this.isTopReport ? 59 : 54;
    },
    listHeight() {
      return this.listItemHeight * 8;
    }
  },

  watch: {
    async isOpen(newVal) {
      if (newVal) {
        await this.fetchKeywords();

        if (this.canSelectMultipleKeywords) {
          this.selectedKeywords = [...this.initiallySelectedKeywords];
        } else if (
          this.isAverageReport &&
          this.initiallySelectedKeywords &&
          this.initiallySelectedKeywords.length
        ) {
          this.selectedKeywords = [this.initiallySelectedKeywords[0]];
        }
      } else {
        this.selectedKeywords = [];
      }
    }
  },

  methods: {
    async fetchKeywords() {
      this.loading = true;
      await this.$store.dispatch("keywords/fetchKeywords", { all: true });
      this.loading = false;
    },
    toggleAllKeywords() {
      if (this.isAllKeywordsSelected) {
        this.selectedKeywords = [];
      } else {
        this.selectedKeywords = [...this.filteredKeywords];
      }
    },
    toggleKeyword(item) {
      const foundIndex = this.selectedKeywords.findIndex(el => {
        return el.keyword.id === item.keyword.id;
      });
      if (foundIndex === -1) {
        this.selectedKeywords.push(item);
      } else {
        this.selectedKeywords.splice(foundIndex, 1);
      }
    },
    selectKeyword(item) {
      this.selectedKeywords = [item];
      this.selectedKeywordId = item.keyword.id;
    },
    save() {
      this.$emit("save", [...this.selectedKeywords]);
      this.close();
    },
    close() {
      this.$emit("close");
    }
  }
};
</script>

<style lang="scss" scoped>
.dialog-body {
  position: relative;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  .list-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-right: 0 !important;
    padding-left: $spacing * 1.5;
    width: 100%;
    height: 54px;
    border-bottom: 1px solid $light-3;

    &--top {
      height: 59px;
    }

    &__name {
      @include font-size(14px);
      margin-bottom: 0;
      line-height: 24px;
    }

    .v-list-item__content {
      padding-left: $spacing;
    }

    .v-input--checkbox {
      margin-top: $spacing * 2.5;
    }
  }

  .v-radio--is-disabled > * {
    opacity: 0.5;
  }
}

header {
  padding-left: $spacing * 1.5;
  border-bottom: 1px solid $light-3;
  margin-bottom: $spacing * 1.5;

  h3 {
    @include font-size(22px);
    margin-bottom: $spacing * 1.5;
  }

  p {
    @include font-size(16px);
    line-height: 19.5px;
    margin-bottom: 0 !important;
    opacity: 0.9;
  }
}

.v-label {
  padding-right: $spacing;
}

.dialog-header {
  margin-right: 0 !important;
}

.v-input--selection-controls {
  margin-top: 0;
}

.v-input--selection-controls__input {
  margin-right: $spacing * 1.5;
}

.v-card__title {
  padding-bottom: $spacing * 3;

  h3 {
    line-height: 36px;
  }
}
</style>
